// src/store/slices/expressionsSlice.ts
/**
 * Expression Objects Redux Slice (T-17)
 * 
 * Manages mathematical expression objects that appear on the number line.
 * These objects evaluate expressions and show their positions visually,
 * with automatic updates when variable values change.
 */

import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../types/redux';
import { evaluate, getVariableReferences, parse, type Environment } from '../../lib/expressions';
import { toNumber } from '../../lib/fractions';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

export interface ExpressionObject {
  id: string;
  expression: string;        // Mathematical expression (e.g., "a + 2", "x^2")
  position: number | null;   // Evaluated position on number line (null if invalid)
  variables: string[];       // Variables this expression depends on
  isSelected: boolean;       // Whether this object is currently selected
  style: ExpressionStyle;    // Visual appearance settings
  createdAt: number;         // Timestamp for ordering
  /** Optional equivalent fractions line context (denominator). */
  equivalentDenominator?: number;
}

export interface ExpressionStyle {
  color: string;
  size: 'small' | 'medium' | 'large';
  showLabel: boolean;
  showValue: boolean;
  opacity: number;
}

export interface ExpressionWidget {
  isVisible: boolean;
  position: { x: number; y: number };  // Screen coordinates
  mode: 'create' | 'edit';
  currentExpression: string;
  targetObjectId?: string;   // When editing existing object
  previewPosition: number | null;  // Live preview position
  equivalentDenominator?: number | null;
}

export interface ExpressionsState {
  objects: ExpressionObject[];
  environment: Environment;           // Variable values from expression evaluations
  selectedObjectIds: string[];       // Multiple selection support
  widget: ExpressionWidget;
  dependencies: Record<string, string[]>;  // objectId -> variable names it depends on
  isEnabled: boolean;                 // Global toggle for expression objects
  nextId: number;                     // For generating unique IDs
}

// =============================================================================
// INITIAL STATE
// =============================================================================

const defaultExpressionStyle: ExpressionStyle = {
  color: '#8B5CF6', // Purple-500
  size: 'medium',
  showLabel: true,
  showValue: true,
  opacity: 1.0,
};

const defaultWidget: ExpressionWidget = {
  isVisible: false,
  position: { x: 0, y: 0 },
  mode: 'create',
  currentExpression: '',
  previewPosition: null,
  equivalentDenominator: null,
};

const initialState: ExpressionsState = {
  objects: [],
  environment: {},
  selectedObjectIds: [],
  widget: defaultWidget,
  dependencies: {},
  isEnabled: true,
  nextId: 1,
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Evaluate an expression and return its position on the number line
 */
function evaluateExpressionPosition(expression: string, env: Environment): number | null {
  try {
    const result = evaluate(expression, env);
    if (result.error || !result.value) {
      return null;
    }
    return toNumber(result.value);
  } catch (error) {
    return null;
  }
}

/**
 * Update all expression objects when environment changes
 */
function updateObjectPositions(objects: ExpressionObject[], env: Environment): ExpressionObject[] {
  return objects.map(obj => ({
    ...obj,
    position: evaluateExpressionPosition(obj.expression, env),
    variables: getVariableReferences(parse(obj.expression)),
  }));
}

/**
 * Generate dependencies map for all objects
 */
function generateDependencies(objects: ExpressionObject[]): Record<string, string[]> {
  const deps: Record<string, string[]> = {};
  for (const obj of objects) {
    try {
      deps[obj.id] = getVariableReferences(parse(obj.expression));
    } catch (error) {
      deps[obj.id] = [];
    }
  }
  return deps;
}

// =============================================================================
// REDUX SLICE
// =============================================================================

const expressionsSlice = createSlice({
  name: 'expressions',
  initialState,
  reducers: {
    // Object Management
    addExpressionObject: (state, action: PayloadAction<{
      expression: string;
      screenPosition: { x: number; y: number };
      style?: Partial<ExpressionStyle>;
      equivalentDenominator?: number | null;
    }>) => {
      const { expression, screenPosition, style = {}, equivalentDenominator = null } = action.payload;
      
      const newObject: ExpressionObject = {
        id: `expr-${state.nextId}`,
        expression: expression.trim(),
        position: evaluateExpressionPosition(expression.trim(), state.environment),
        variables: [],
        isSelected: false,
        style: { ...defaultExpressionStyle, ...style },
        createdAt: Date.now(),
        equivalentDenominator: equivalentDenominator ?? undefined,
      };

      // Calculate variables
      try {
        newObject.variables = getVariableReferences(parse(expression.trim()));
      } catch (error) {
        newObject.variables = [];
      }

      state.objects.push(newObject);
      state.dependencies[newObject.id] = newObject.variables;
      state.nextId += 1;

      // Auto-select the new object
      state.selectedObjectIds = [newObject.id];
    },

    updateExpressionObject: (state, action: PayloadAction<{
      id: string;
      updates: Partial<Pick<ExpressionObject, 'expression' | 'style'>>;
    }>) => {
      const { id, updates } = action.payload;
      const object = state.objects.find(obj => obj.id === id);
      
      if (object) {
        // Update expression if provided
        if (updates.expression !== undefined) {
          object.expression = updates.expression.trim();
          object.position = evaluateExpressionPosition(object.expression, state.environment);
          
          try {
            object.variables = getVariableReferences(parse(object.expression));
            state.dependencies[id] = object.variables;
          } catch (error) {
            object.variables = [];
            state.dependencies[id] = [];
          }
        }

        // Update style if provided
        if (updates.style) {
          object.style = { ...object.style, ...updates.style };
        }
      }
    },

    deleteExpressionObject: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      state.objects = state.objects.filter(obj => obj.id !== id);
      delete state.dependencies[id];
      state.selectedObjectIds = state.selectedObjectIds.filter(selectedId => selectedId !== id);
    },

    deleteSelectedExpressionObjects: (state) => {
      for (const id of state.selectedObjectIds) {
        state.objects = state.objects.filter(obj => obj.id !== id);
        delete state.dependencies[id];
      }
      state.selectedObjectIds = [];
    },

    // Selection Management
    selectExpressionObject: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      state.selectedObjectIds = [id];
      
      // Update selection state on objects
      state.objects.forEach(obj => {
        obj.isSelected = obj.id === id;
      });
    },

    toggleExpressionObjectSelection: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const isSelected = state.selectedObjectIds.includes(id);
      
      if (isSelected) {
        state.selectedObjectIds = state.selectedObjectIds.filter(selectedId => selectedId !== id);
      } else {
        state.selectedObjectIds.push(id);
      }

      // Update selection state on objects
      state.objects.forEach(obj => {
        obj.isSelected = state.selectedObjectIds.includes(obj.id);
      });
    },

    deselectAllExpressionObjects: (state) => {
      state.selectedObjectIds = [];
      state.objects.forEach(obj => {
        obj.isSelected = false;
      });
    },

    /**
     * Toggle expression selection (supports multi-selection with Ctrl/Cmd)
     */
    toggleExpressionSelection: (state, action: PayloadAction<{ id: string; ctrlKey?: boolean }>) => {
      const { id, ctrlKey } = action.payload;
      const object = state.objects.find(obj => obj.id === id);
      if (object) {
        if (!ctrlKey) {
          // Clear all selections if Ctrl is not held
          state.objects.forEach(obj => obj.isSelected = false);
          state.selectedObjectIds = [];
        }
        object.isSelected = !object.isSelected;
        
        // Update selectedObjectIds array
        if (object.isSelected) {
          if (!state.selectedObjectIds.includes(id)) {
            state.selectedObjectIds.push(id);
          }
        } else {
          state.selectedObjectIds = state.selectedObjectIds.filter(selectedId => selectedId !== id);
        }
      }
    },

    // Environment Management (Variable Changes)
    updateEnvironment: (state, action: PayloadAction<Environment>) => {
      state.environment = { ...state.environment, ...action.payload };
      
      // Recalculate all object positions
      state.objects = updateObjectPositions(state.objects, state.environment);
    },

    setEnvironmentVariable: (state, action: PayloadAction<{ name: string; value: any }>) => {
      const { name, value } = action.payload;
      state.environment[name] = value;
      
      // Update all objects that depend on this variable
      state.objects.forEach(obj => {
        // Re-calculate variables to ensure they're up to date
        try {
          obj.variables = getVariableReferences(parse(obj.expression));
        } catch (error) {
          obj.variables = [];
        }
        
        // Update position if this object depends on the changed variable
        if (obj.variables.includes(name)) {
          obj.position = evaluateExpressionPosition(obj.expression, state.environment);
        }
      });
    },

    // Widget Management
    showExpressionWidget: (state, action: PayloadAction<{
      position: { x: number; y: number };
      mode: 'create' | 'edit';
      expression?: string;
      objectId?: string;
      equivalentDenominator?: number | null;
    }>) => {
      const { position, mode, expression = '', objectId, equivalentDenominator = null } = action.payload;
      
      state.widget = {
        isVisible: true,
        position,
        mode,
        currentExpression: expression,
        targetObjectId: objectId,
        previewPosition: evaluateExpressionPosition(expression, state.environment),
        equivalentDenominator,
      };
    },

    hideExpressionWidget: (state) => {
      state.widget = defaultWidget;
    },

    updateWidgetExpression: (state, action: PayloadAction<string>) => {
      state.widget.currentExpression = action.payload;
      state.widget.previewPosition = evaluateExpressionPosition(action.payload, state.environment);
    },

    // Global Settings
    toggleExpressionsEnabled: (state) => {
      state.isEnabled = !state.isEnabled;
    },

    clearAllExpressions: (state) => {
      state.objects = [];
      state.selectedObjectIds = [];
      state.dependencies = {};
      state.widget = defaultWidget;
    },

    /**
     * Load expressions state from shared scene
     */
    loadExpressionsState: (state, action: PayloadAction<{
      objects: ExpressionObject[];
      environment: Environment;
      dependencies: Record<string, string[]>;
      isEnabled: boolean;
      nextId: number;
    }>) => {
      const { objects, environment, dependencies, isEnabled, nextId } = action.payload;
      state.objects = objects.map(obj => ({
        ...obj,
        isSelected: false, // Reset selection on load
      }));
      state.environment = environment;
      state.dependencies = dependencies;
      state.isEnabled = isEnabled;
      state.nextId = nextId;
      state.selectedObjectIds = [];
      state.widget = defaultWidget;
    },
  },
});

// =============================================================================
// ACTIONS & SELECTORS
// =============================================================================

export const {
  addExpressionObject,
  updateExpressionObject,
  deleteExpressionObject,
  deleteSelectedExpressionObjects,
  selectExpressionObject,
  toggleExpressionObjectSelection,
  deselectAllExpressionObjects,
  toggleExpressionSelection,
  updateEnvironment,
  setEnvironmentVariable,
  showExpressionWidget,
  hideExpressionWidget,
  updateWidgetExpression,
  toggleExpressionsEnabled,
  clearAllExpressions,
  loadExpressionsState,
} = expressionsSlice.actions;

// Selectors
export const selectExpressions = (state: RootState) => state.expressions;
export const selectExpressionObjects = (state: RootState) => state.expressions.objects;
export const selectSelectedExpressionObjects = createSelector(
  [selectExpressionObjects],
  (objects) => objects.filter(obj => obj.isSelected)
);
export const selectExpressionEnvironment = (state: RootState) => state.expressions.environment;
export const selectExpressionWidget = (state: RootState) => state.expressions.widget;
export const selectExpressionsEnabled = (state: RootState) => state.expressions.isEnabled;

// Complex selectors
export const selectExpressionObjectsInDomain = (domain: { start: number; end: number }) => 
  (state: RootState) => {
    return state.expressions.objects.filter(obj => 
      obj.position !== null && 
      obj.position >= domain.start && 
      obj.position <= domain.end
    );
  };

export const selectVariablesInUse = createSelector(
  [(state: RootState) => state.expressions.dependencies],
  (dependencies) => {
    const variables = new Set<string>();
    for (const deps of Object.values(dependencies)) {
      deps.forEach((variable: string) => variables.add(variable));
    }
    return Array.from(variables).sort();
  }
);

export default expressionsSlice.reducer;